import type {
  AdminOrder,
  AppConfig,
  CartQuote,
  Me,
  NewOrder,
  Order,
  OrderList,
  OrderStatus,
  OrderStatusUpdate,
  Product,
  ProfileUpdate,
} from "@bioflow/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { hasTelegramContext } from "../lib/telegram";

/**
 * Запросы, требующие авторизации, не отправляются вне Telegram: без initData
 * они гарантированно вернут 401, а react-query устроил бы на этом ретраи.
 */
const authOnly = { enabled: hasTelegramContext() };

export const PRODUCT_SLUG = "tirzepatide";

export const queryKeys = {
  me: ["me"] as const,
  config: ["config"] as const,
  product: ["product", PRODUCT_SLUG] as const,
  quote: (variantId: string, quantity: number) => ["quote", variantId, quantity] as const,
  orders: ["orders"] as const,
  order: (orderNumber: string) => ["order", orderNumber] as const,
  adminOrders: (status: OrderStatus | null) => ["admin-orders", status] as const,
  adminOrder: (orderNumber: string) => ["admin-order", orderNumber] as const,
};

export const useMe = () =>
  useQuery({ queryKey: queryKeys.me, queryFn: () => api<Me>("/auth/me"), ...authOnly });

export const useConfig = () =>
  useQuery({
    queryKey: queryKeys.config,
    queryFn: () => api<AppConfig>("/config", { anonymous: true }),
    staleTime: Infinity,
  });

export const useProduct = () =>
  useQuery({
    queryKey: queryKeys.product,
    queryFn: () => api<Product>(`/products/${PRODUCT_SLUG}`, { anonymous: true }),
  });

/** Пересчёт выбора сервером: цена и остаток могли измениться. */
export const useQuote = (variantId: string, quantity: number) =>
  useQuery({
    queryKey: queryKeys.quote(variantId, quantity),
    queryFn: () =>
      api<CartQuote>("/cart/quote", {
        method: "POST",
        body: { items: [{ variantId, quantity }] },
        anonymous: true,
      }),
    enabled: variantId.length > 0 && quantity > 0,
    // Сумма под формой не должна мигать «загрузкой» при смене количества.
    placeholderData: (previous) => previous,
  });

export const useOrders = () =>
  useQuery({
    queryKey: queryKeys.orders,
    queryFn: () => api<OrderList>("/me/orders"),
    ...authOnly,
  });

/**
 * Заказ, который ещё в пути, опрашивается раз в полминуты: менеджер двигает
 * статус со своей стороны, и экран должен обновиться сам.
 */
export const useOrder = (orderNumber: string) =>
  useQuery({
    queryKey: queryKeys.order(orderNumber),
    queryFn: () => api<Order>(`/me/orders/${encodeURIComponent(orderNumber)}`),
    enabled: hasTelegramContext() && orderNumber.length > 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && status !== "delivered" && status !== "cancelled" ? 30_000 : false;
    },
  });

export const usePlaceOrder = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ order, idempotencyKey }: { order: NewOrder; idempotencyKey: string }) =>
      api<Order>("/orders", {
        method: "POST",
        body: order,
        headers: { "idempotency-key": idempotencyKey },
      }),
    onSuccess: (order) => {
      client.setQueryData(queryKeys.order(order.orderNumber), order);
      void client.invalidateQueries({ queryKey: queryKeys.orders });
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
};

/** Данные онбординга: сервер отвечает обновлённым профилем — кладём его в кэш сразу. */
export const useUpdateProfile = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfileUpdate) => api<Me>("/me/profile", { method: "PATCH", body: patch }),
    onSuccess: (me) => client.setQueryData(queryKeys.me, me),
  });
};

/* --- Админка: авторизация по роли --- */

export const useAdminOrders = (status: OrderStatus | null) =>
  useQuery({
    queryKey: queryKeys.adminOrders(status),
    queryFn: () => api<OrderList>(`/admin/orders?limit=100${status ? `&status=${status}` : ""}`),
    ...authOnly,
  });

export const useAdminOrder = (orderNumber: string) =>
  useQuery({
    queryKey: queryKeys.adminOrder(orderNumber),
    queryFn: () => api<AdminOrder>(`/admin/orders/${encodeURIComponent(orderNumber)}`),
    enabled: hasTelegramContext() && orderNumber.length > 0,
  });

export const useAdminStatus = (orderNumber: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: OrderStatusUpdate) =>
      api<Order>(`/admin/orders/${encodeURIComponent(orderNumber)}/status`, {
        method: "PATCH",
        body: update,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.adminOrder(orderNumber) });
      void client.invalidateQueries({ queryKey: ["admin-orders"] });
    },
  });
};
